/**
 * Holme & Moss — Custom JS
 * Migration-safe extensions for Prestige theme.
 * Loaded with defer — will not conflict with theme updates.
 */

/* ──────────────────────────────────────────────
 * Judge.me Reviews — Accordion Integration
 * Opens the Reviews accordion on product pages when:
 *   1. The review badge (star rating) is clicked
 *   2. The URL contains a Judge.me token (?jdgm_token)
 * Also hides the sticky ATC bar while the review form is open.
 * ────────────────────────────────────────────── */

/**
 * Detect a Judge.me review-invitation link.
 * Judge.me has used two parameter schemes over time:
 *   - legacy: ?jdgm_token=…
 *   - current: ?judgeme_token=… (often with &judgeme_review_source=…)
 * Match either so invite emails keep opening the Reviews accordion across
 * Judge.me updates. Also honour a #judgeme hash for manual deep-links.
 */
function hasReviewInviteToken() {
  const search = window.location.search;
  return (
    search.includes('jdgm_token') ||
    search.includes('judgeme_token') ||
    search.includes('judgeme_review_source') ||
    window.location.hash.includes('judgeme')
  );
}

/**
 * Find and open the Reviews accordion by looking for an accordion
 * whose title contains "Review" (case-insensitive).
 * Prestige uses <accordion-disclosure> with <details>/<summary>.
 */
function openReviewsAccordion(scroll) {
  if (scroll === undefined) scroll = true;
  const toggles = document.querySelectorAll('accordion-disclosure .accordion__toggle');

  for (const toggle of toggles) {
    const titleSpan = toggle.querySelector('.text-with-icon, span');
    const titleText = titleSpan ? titleSpan.textContent.trim() : toggle.textContent.trim();
    if (!titleText.toLowerCase().includes('review')) continue;

    const details = toggle.closest('details');
    if (!details) continue;

    // Force open deterministically. Prestige's <accordion-disclosure> animates
    // off the `open` attribute, so setting it is enough. Critically we do NOT
    // dispatch a click: a click *toggles*, and this function is called from
    // several places — two near-simultaneous toggles would cancel out and leave
    // the accordion shut (the original invite-link bug). Setting `open` is
    // idempotent, so repeated calls are safe.
    if (!details.open) {
      details.open = true;
      details.setAttribute('aria-expanded', 'true');
    }

    if (scroll) {
      setTimeout(function () {
        toggle.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
    return toggle;
  }

  return null;
}

/**
 * Open the Reviews accordion from a review-invitation link and hold the scroll
 * on it. Judge.me runs its own scroll (which lands at the page bottom) for ~1s
 * after detecting the invite token, so a one-off scroll loses the race. We snap
 * back with rAF for 1.5s — re-asserting the open each frame in case Judge.me
 * re-renders the widget mid-flight. Mirrors the pagination scroll-hold below.
 */
function openReviewsFromInvite() {
  const toggle = openReviewsAccordion(false);
  if (!toggle) return false;

  const start = performance.now();
  (function hold() {
    openReviewsAccordion(false);
    const targetY = toggle.getBoundingClientRect().top + window.scrollY - 120;
    if (Math.abs(window.scrollY - targetY) > 2) {
      window.scrollTo({ top: targetY, behavior: 'auto' });
    }
    if (performance.now() - start < 1500) requestAnimationFrame(hold);
  })();
  return true;
}

/**
 * Manage sticky ATC bar visibility when Judge.me review form is shown/hidden.
 */
function manageStickyBar() {
  const stickyBar = document.querySelector('.product-sticky-bar, sticky-add-to-cart');
  if (!stickyBar) return;

  const observer = new MutationObserver(function () {
    const reviewForm = document.querySelector('.jdgm-form-wrapper, .jdgm-rev-widg__write-rev-form');
    if (reviewForm) {
      const isVisible = reviewForm.offsetHeight > 0;
      stickyBar.style.display = isVisible ? 'none' : '';
    }
  });

  observer.observe(document.body, { childList: true, subtree: true, attributes: true });
}

// Handle review badge clicks — use capture phase to fire before Judge.me's own handlers
document.addEventListener('click', function (e) {
  var el = e.target;
  while (el && el !== document) {
    if (el.className && typeof el.className === 'string' &&
        el.className.indexOf('jdgm-prev-badge') !== -1) {
      openReviewsAccordion();
      return;
    }
    el = el.parentElement;
  }
}, true);

// Auto-open reviews if URL has a Judge.me invite token (e.g. review invitation
// link). The accordion exists at DOMContentLoaded; the jdgm.doneSetup handler
// below re-runs this once Judge.me has rendered and done its own (bottom-bound)
// scroll, so we win the final position.
if (hasReviewInviteToken()) {
  document.addEventListener('DOMContentLoaded', function () {
    setTimeout(openReviewsFromInvite, 300);
  });
}

/* ──────────────────────────────────────────────
 * Product Gallery — Global Arrow Key Navigation
 * Lets desktop users flick through product images with ← / → without
 * needing to click into the gallery or open the lightbox first.
 * ────────────────────────────────────────────── */
document.addEventListener('keydown', function (e) {
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

  // Skip when user is typing in a form field
  const target = e.target;
  if (target && (target.matches('input, textarea, select, [contenteditable="true"]'))) return;

  // Skip when a dialog / drawer / lightbox is open — let them handle their own keys
  if (document.querySelector('dialog[open], .product-lightbox[open], x-drawer[open]')) return;

  // Only active on product pages — look for the gallery's scroll-carousel
  const gallery = document.querySelector('product-gallery scroll-carousel');
  if (!gallery) return;

  if (e.key === 'ArrowLeft') {
    gallery.previous();
  } else {
    gallery.next();
  }
});

/**
 * Format Judge.me review count numbers with thousands separators.
 * Judge.me renders raw integers like "from 1237 reviews" — we want "1,237".
 * Runs idempotently: once formatted, the regex won't match (commas break \d{4,}).
 */
function formatReviewCounts() {
  // Walk every text node inside the Judge.me review-count widgets and rewrite
  // 4+ digit runs in place. Operating on text nodes (not elements) means we
  // never touch element structure — stars, links, click handlers stay intact.
  // Different Judge.me widgets use different class names; cover all known
  // homes for review-count text. The TreeWalker only mutates text nodes
  // matching the regex, so adding broad selectors is safe.
  const containers = document.querySelectorAll(
    // Carousel / "What our customers say" widget
    '.jdgm-all-reviews-rating-wrapper, ' +
    // Product page review preview badge
    '.jdgm-prev-badge, ' +
    // Dedicated /pages/reviews summary block
    '.jdgm-all-reviews__summary-text, ' +
    // Subtab counters ("Product Reviews (1235)" / "Shop Reviews (2)")
    '.jdgm-subtab__count'
  );
  const COMMA_RE = /\b(\d{4,})\b/g;

  for (const container of containers) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (!COMMA_RE.test(node.nodeValue)) continue;
      COMMA_RE.lastIndex = 0;
      node.nodeValue = node.nodeValue.replace(COMMA_RE, function (n) {
        return Number(n).toLocaleString('en-GB');
      });
    }
  }
}

// Watch for Judge.me to populate review counts (its setup event fires before
// the count is rendered in some widgets). Disconnect after 10s — if it hasn't
// rendered by then, it's not going to.
(function watchReviewCounts() {
  const observer = new MutationObserver(formatReviewCounts);
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  setTimeout(function () { observer.disconnect(); }, 10000);
})();

// When Judge.me finishes setup, set up the sticky bar management
document.addEventListener('jdgm.doneSetup', function () {
  manageStickyBar();
  formatReviewCounts();

  // Invite link: open + hold the scroll on the reviews accordion. Judge.me's own
  // scroll fires around now and lands at the page bottom, so we must out-scroll it.
  if (hasReviewInviteToken()) {
    openReviewsFromInvite();
  }
});

/* ──────────────────────────────────────────────
 * Judge.me pagination — scroll to the top of the reviews accordion when the
 * user changes pages, so the first review of the new page is visible.
 * ────────────────────────────────────────────── */
// Poll for the active page number every 200ms; on change, scroll to the top
// of the reviews accordion. We can't use click events (Judge.me intercepts)
// or a one-off scroll (Judge.me runs its own scroll for ~1s after pagination).
// Hold position with rAF for 1200ms, snapping back if anything else scrolls.
let lastSeenPage = null;
setInterval(function () {
  const cur = document.querySelector('.jdgm-paginate__page.jdgm-curt');
  if (!cur) return;
  const pageNum = cur.dataset.page;
  if (pageNum === lastSeenPage) return;
  const previousPage = lastSeenPage;
  lastSeenPage = pageNum;
  if (previousPage === null) return;

  const widget = cur.closest('.jdgm-rev-widg');
  const anchor =
    document.getElementById('jdgmbt') ||
    (widget && widget.closest('details')) ||
    (widget && widget.closest('accordion-disclosure')) ||
    widget;
  if (!anchor) return;

  const targetY = anchor.getBoundingClientRect().top + window.scrollY - 120;
  const start = performance.now();
  (function hold() {
    if (Math.abs(window.scrollY - targetY) > 2) {
      window.scrollTo({ top: targetY, behavior: 'auto' });
    }
    if (performance.now() - start < 1200) requestAnimationFrame(hold);
  })();
}, 200);

/* ──────────────────────────────────────────────
 * Featured product ("Product of the week") — make the gallery image
 * clickable, linking to the product page (same URL as the title).
 * Uses event delegation so it works even if the gallery re-renders.
 * ────────────────────────────────────────────── */
function wireFeaturedProductClick() {
  const sections = document.querySelectorAll('.shopify-section[id*="featured_product"], .shopify-section--featured-product');
  for (const section of sections) {
    if (section.dataset.hmFpWired) continue;
    const titleLink = section.querySelector('.product-info a[href*="/products/"], .product-info__title a[href*="/products/"]');
    const gallery = section.querySelector('product-gallery, .product-gallery');
    if (!titleLink || !gallery) continue;
    section.dataset.hmFpWired = '1';
    gallery.style.cursor = 'pointer';
    gallery.addEventListener('click', function (e) {
      // Don't hijack clicks on actual buttons/links inside the gallery
      // (zoom button, thumbnail nav arrows etc.)
      if (e.target.closest('a, button, [role="button"]')) return;
      window.location.href = titleLink.href;
    });
  }
}
document.addEventListener('DOMContentLoaded', wireFeaturedProductClick);
// Run again on theme-editor reloads / late renders
new MutationObserver(wireFeaturedProductClick).observe(document.body, {
  childList: true,
  subtree: true,
});

/* ──────────────────────────────────────────────
 * Inventory text — match live v9.1.0 behaviour
 * Live's inventory.liquid was customised to always show count for low
 * stock ("Only 2 left") but generic "In stock" for normal stock. v11's
 * snippet ties both to a single setting. With show_in_stock_quantity=true
 * we get "Only 2 left" correctly, but "5 in stock" (or "-23 in stock")
 * for in-stock — replace those with "In stock" to match live.
 * ────────────────────────────────────────────── */
function normalizeInventoryText() {
  const spans = document.querySelectorAll('variant-inventory span');
  for (const span of spans) {
    if (/^(-?\d+)\s+in stock$/i.test(span.textContent.trim())) {
      span.textContent = 'In stock';
    }
  }
  // Mark each variant-inventory ready so the CSS reveal kicks in
  for (const el of document.querySelectorAll('variant-inventory')) {
    el.classList.add('hm-inv-ready');
  }
}

document.addEventListener('DOMContentLoaded', normalizeInventoryText);
document.addEventListener('variant:change', normalizeInventoryText);

// Catch late renders / Shopify section reloads
new MutationObserver(normalizeInventoryText).observe(document.body, {
  childList: true,
  subtree: true,
  characterData: true,
});

/* ──────────────────────────────────────────────
 * Footer VAT number — legal requirement. The copyright line in
 * sections/footer.liquid ("© {year} - {shop.name}") is hardcoded with no
 * setting to append text, and that file is core/read-only. Inject the VAT
 * registration as real DOM text (selectable, present in source) on its own
 * line beneath the copyright. Re-runs on theme-editor section reloads.
 * ────────────────────────────────────────────── */
function appendFooterVat() {
  const aside = document.querySelector('.footer__aside');
  if (!aside) return;
  // Copyright is a direct child <p>; localization selectors are nested deeper.
  const copyright = aside.querySelector(':scope > p') || aside.querySelector('p');
  if (!copyright || copyright.querySelector('.hm-vat')) return;
  const vat = document.createElement('span');
  vat.className = 'hm-vat';
  vat.textContent = 'VAT GB517759849';
  copyright.appendChild(vat);
}

document.addEventListener('DOMContentLoaded', appendFooterVat);
document.addEventListener('shopify:section:load', appendFooterVat);

/* ──────────────────────────────────────────────
 * Product recommendations — close the crawl trap
 *
 * Shopify appends attribution params to every "you may also like" link:
 *   ?pr_prod_strat=jac&pr_rec_id=b0912d3dc&pr_rec_pid=…&pr_ref_pid=…&pr_seq=uniform
 *
 * `pr_rec_id` identifies a single render and rotates over time, so the same
 * product+referrer pair yields endless distinct URLs. Confirmed in GA4: the
 * same product with identical pr_ref_pid/pr_prod_strat counted twice under two
 * pr_rec_id values. A headless-Chrome crawler rode that into 9,287 distinct
 * landing pages in one day (29 Jul 2026), inflating session counts and pushing
 * desktop LCP p75 to ~7s.
 *
 * The links are injected client-side, so they're invisible to plain crawlers
 * and to robots.txt-respecting ones alike — only a JS-executing crawler ever
 * sees them, which is exactly what hit us. Rewriting the DOM is therefore the
 * only lever that reaches the offender. (A robots.txt override was tried and
 * reverted: see jarvis Tried & Rejected.)
 *
 * We drop ONLY pr_rec_id. The four params Shopify documents for its
 * "Product recommendation conversion over time" report — pr_prod_strat,
 * pr_rec_pid, pr_ref_pid, pr_seq — are left intact, so conversion attribution
 * still works while the URL space becomes finite instead of unbounded.
 * ────────────────────────────────────────────── */
const RECOMMENDATION_HOSTS = 'product-recommendations, complementary-products';

/**
 * Remove the volatile pr_rec_id param from recommendation links under `root`.
 * Leaves the path and every other param untouched.
 */
function stripRecommendationRenderId(root) {
  root.querySelectorAll?.('a[href*="pr_rec_id="]').forEach((link) => {
    const href = link.getAttribute('href');
    if (!href) return;
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    url.searchParams.delete('pr_rec_id');
    link.setAttribute('href', url.pathname + url.search);
  });
}

/**
 * Clean a recommendations container now, and again whenever it re-renders.
 * The container ships empty in the server HTML and fills in via fetch, so the
 * observer — not the initial pass — is what usually does the work.
 */
function watchRecommendationHost(host) {
  if (host.dataset.hmPrWatched) return;
  host.dataset.hmPrWatched = 'true';
  stripRecommendationRenderId(host);
  new MutationObserver(() => stripRecommendationRenderId(host)).observe(host, {
    childList: true,
    subtree: true,
  });
}

function initRecommendationLinkCleanup(scope = document) {
  scope.querySelectorAll?.(RECOMMENDATION_HOSTS).forEach(watchRecommendationHost);
}

document.addEventListener('DOMContentLoaded', () => initRecommendationLinkCleanup());
document.addEventListener('shopify:section:load', (event) =>
  initRecommendationLinkCleanup(event.target)
);

/* ──────────────────────────────────────────────
 * Blog comments — inline replies
 *
 * Shopify blog comments are flat. Neither the REST Comment resource nor the
 * GraphQL Comment object carries a parent field, so when Anna answers someone
 * the answer is posted as another top-level comment. `article.comments` then
 * renders newest-first, which puts the answer ABOVE the question it answers.
 *
 * We can't fix that where it belongs. sections/main-article.liquid is a core
 * Prestige file and editing it forfeits automatic theme updates, so the pairing
 * is done here instead, against the rendered markup.
 *
 * A comment is treated as ours when the author matches OWNER_AUTHORS, or when
 * the body opens with the ↩ marker Anna has typed by hand since March 2025.
 * Author is the primary test because it needs no cooperation from the comment
 * body — the marker is a fallback for anything posted under a different name.
 *
 * Each reply is moved directly beneath the comment it answers and tagged
 * .comment--reply for the indent (see custom.css). Thread order is otherwise
 * left exactly as Shopify sorted it, so the most recent exchange stays on top.
 * ────────────────────────────────────────────── */
const OWNER_AUTHORS = ['holme & moss', 'anna - holme & moss'];

/* U+21A9 with an optional variation selector, so it matches whether or not the
   emoji presentation byte came along. */
const REPLY_MARKER = /^\s*↩️?\s*/;

function commentAuthor(comment) {
  // Scoped to .h-stack — .prose holds the body, which has <p> tags of its own.
  const author = comment.querySelector('.h-stack p');
  return author ? author.textContent.trim().toLowerCase() : '';
}

function isOwnerReply(comment) {
  // Threaded by an earlier pass. Checked first because stripReplyMarker() has
  // already removed the ↩ by then, so the marker test below would miss it.
  if (comment.classList.contains('comment--reply')) return true;
  if (OWNER_AUTHORS.includes(commentAuthor(comment))) return true;
  const body = comment.querySelector('.prose');
  return !!body && REPLY_MARKER.test(body.textContent);
}

/**
 * Drop the ↩ prefix from a reply body. Once the comment is visibly indented
 * under the one it answers the marker is just noise.
 *
 * Edits the first non-empty text node rather than the .prose innerHTML, so
 * whatever markup Shopify wrapped the body in survives untouched.
 */
function stripReplyMarker(comment) {
  const body = comment.querySelector('.prose');
  if (!body) return;

  const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node && !node.nodeValue.trim()) node = walker.nextNode();

  if (node && REPLY_MARKER.test(node.nodeValue)) {
    node.nodeValue = node.nodeValue.replace(REPLY_MARKER, '');
  }
}

function threadBlogComments(scope) {
  const list = (scope || document).querySelector('.article__comments .divide-y');
  if (!list) return;

  const comments = Array.from(list.querySelectorAll(':scope > .comment'));
  if (comments.length < 2) return;

  // Resolve every comment up front: the pairing below reads neighbours while it
  // reorders them, so this must not be recomputed against a half-moved list.
  const replies = new Map(comments.map((comment) => [comment, isOwnerReply(comment)]));

  const isReply = (el) => !!el && replies.get(el) === true;
  const isParent = (el) => !!el && replies.get(el) === false;

  comments.forEach((comment) => {
    if (!isReply(comment)) return;

    // Threaded by an earlier pass over this same DOM — already in place.
    //
    // Do NOT infer that from "the comment above is a customer comment". In
    // newest-first order an untouched reply is ABOVE its parent, and the
    // comment above it is simply the next customer comment to arrive. Judith's
    // reply (20 Aug) indented under Helen's later comment (26 Aug) that way.
    if (comment.classList.contains('comment--reply')) return;

    // Newest-first render order: the parent is the next non-reply below, past
    // any sibling replies to that same comment.
    let parent = comment.nextElementSibling;
    while (isReply(parent)) parent = parent.nextElementSibling;

    // No parent found (an owner comment that opens a thread) — leave it alone.
    if (!isParent(parent)) return;

    parent.after(comment);
    comment.classList.add('comment--reply');
    stripReplyMarker(comment);
  });
}

/* Runs as early as the parsed DOM allows. custom.js is deferred, so on a normal
   page load the document is already parsed by this point and the reorder lands
   before first paint rather than a frame after it. */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => threadBlogComments());
} else {
  threadBlogComments();
}

document.addEventListener('shopify:section:load', (event) => threadBlogComments(event.target));
